import { FC, useEffect, useState } from 'react'

import { AdjustCanvas } from './AdjustCanvas.component'
import { AdjustHint } from './AdjustHint.component'
import { AdjustZoomControls } from './AdjustZoomControls.component'
import { ExpandedVisualType } from 'components/pages/Gallery/_main/ClientGallery/ClientGalleryPhoto'
import { ManualToolOriginalImagePreview } from '../ManualToolOriginalImagePreview.component'
import { PreviewContainer } from '../PreviewContainer/PreviewContainer.component'
import { Box, Stack } from '@mui/material'
import { UndoRedoPanel } from '../../../common/UndoRedoPanel'
import { VisualTypeSwitcher } from '../../../common/VisualTypeSwitcher'
import { loadDisplayImage } from './adjustImageLoading'
import { useAdjustEditor } from './AdjustEditor.context'
import { useClientPhotoStudio } from '../ClientPhotoStudio.context'
import { useWorkspacePlan } from 'utils/hooks'

/** Same band as UnifiedPreview's Lab view controls, so the photo sits where it does in the other tools */
const LAB_VIEW_CONTROLS_SX = { height: '5rem', marginTop: '2rem', marginBottom: '1.6rem', flexShrink: 0 }

/**
 * The Adjust tool's preview with the in-house editor (replaces ClientPhotoStudioManualToolsPreview when
 * FeatureFlag.PHOTO_STUDIO_PINTURA_REPLACEMENT is on). The view switch and undo / redo render in the tree, with the
 * Photo Studio context — no more React roots injected into Pintura's toolbar.
 *  - Lab engine: Before | Compare | After, drawn on the canvas so Compare shows the live edit. Before = the photo as it
 *    was when Adjust opened while there are unsaved edits, else the shown version's own Before.
 *  - ai-visuals engine: Editing | Original, as before.
 *  - Undo / redo walk the editor's steps first, then the Photo Studio versions.
 */
export const AdjustStage: FC = () => {
  const { hasWorkspaceActiveSubscription } = useWorkspacePlan()
  const {
    isLabSdkEngine,
    isLabCompareAvailable,
    labRunSources,
    expandedVisualType,
    setExpandedVisualType,
    isOriginalVisualType,
    isProcessingImage,
    photoStudioHistory,
    logPhotoStudioEvent,
    assignmentId,
    sourceVisualFilename,
  } = useClientPhotoStudio()
  const editor = useAdjustEditor()
  const { base, hasEdits, viewMode, setViewMode } = editor

  const history = photoStudioHistory.historyState
  const canUndo = editor.canUndo || history.canUndo
  const canRedo = editor.canRedo || (!hasEdits && history.canRedo)
  const isShowingOriginal = !isLabSdkEngine && isOriginalVisualType
  const isCompareAvailable = isLabCompareAvailable || hasEdits

  // Before: the base photo while editing, else the shown version's recorded Before (another image to load)
  const [historyBefore, setHistoryBefore] = useState<HTMLImageElement | null>(null)
  const beforeUrl = isLabSdkEngine && !hasEdits ? labRunSources.before : null
  useEffect(() => {
    if (!beforeUrl || beforeUrl === base?.url) {
      setHistoryBefore(null)
      return
    }
    let isCancelled = false
    loadDisplayImage(beforeUrl)
      .then(image => !isCancelled && setHistoryBefore(image))
      .catch(() => !isCancelled && setHistoryBefore(null))
    return () => {
      isCancelled = true
    }
  }, [beforeUrl, base?.url])
  const before = hasEdits ? base?.image ?? null : historyBefore ?? base?.image ?? null

  // Nothing to compare any more (undone to the start): back to the plain photo
  useEffect(() => {
    if (!isCompareAvailable && viewMode !== 'after') setViewMode('after')
  }, [isCompareAvailable, viewMode, setViewMode])

  return (
    <>

      {(isShowingOriginal || !hasWorkspaceActiveSubscription) && <ManualToolOriginalImagePreview />}

      {hasWorkspaceActiveSubscription && (
        <PreviewContainer hasWorkspaceActiveSubscription={hasWorkspaceActiveSubscription} sx={{ display: isShowingOriginal ? 'none' : 'flex' }}>

          <Stack flexDirection="row" justifyContent="center" alignItems="center" gap=".8rem" sx={{ position: 'relative', flexShrink: 0, ...(isLabSdkEngine ? LAB_VIEW_CONTROLS_SX : {}) }}>

            {!isLabSdkEngine && (
              <VisualTypeSwitcher expandedVisualType={expandedVisualType} switchExpandedVisualType={setExpandedVisualType} />
            )}

            {isLabSdkEngine && (canUndo || canRedo || hasEdits) && (
              <VisualTypeSwitcher
                showCompare
                disabled={!isCompareAvailable}
                expandedVisualType={viewMode === 'before' ? ExpandedVisualType.ORIGINAL : ExpandedVisualType.EDITED}
                isCompareActive={viewMode === 'compare'}
                switchExpandedVisualType={(type) => setViewMode(type === ExpandedVisualType.ORIGINAL ? 'before' : 'after')}
                onCompare={() => setViewMode('compare')}
              />
            )}

            {(!isLabSdkEngine || canUndo || canRedo) && (
              <UndoRedoPanel
                logPhotoStudioEvent={logPhotoStudioEvent}
                assignmentId={assignmentId}
                sourceVisualFilename={sourceVisualFilename}
                disabled={isProcessingImage || isOriginalVisualType}
                canUndo={canUndo}
                canRedo={canRedo}
                onUndo={() => editor.canUndo ? editor.undo() : photoStudioHistory.undo()}
                onRedo={() => editor.canRedo ? editor.redo() : photoStudioHistory.redo()}
                currentHistoryEntry={history.current}
              />
            )}

            {/* Right-aligned on the same row as the view switch and undo / redo */}
            <Box sx={{ position: 'absolute', right: '1.6rem', top: '50%', transform: 'translateY(-50%)' }}>
              <AdjustZoomControls />
            </Box>

          </Stack>

          {/* Same geometry as UnifiedPreview (Stage, Enhance, Erase): 1.6rem sides, then the brush bar's band below */}
          <Stack sx={{ flex: '1 1 0%', minHeight: 0, paddingLeft: '1.6rem', paddingRight: '1.6rem' }}>
            <AdjustCanvas before={before} />
          </Stack>

          <Stack sx={{ flexShrink: 0, height: '5rem', marginTop: '1.6rem', marginBottom: '2rem', alignItems: 'center', justifyContent: 'center', paddingLeft: '1.6rem', paddingRight: '1.6rem' }}>
            <AdjustHint />
          </Stack>

        </PreviewContainer>
      )}

    </>
  )
}
